import { useEffect, useState } from 'react';
import { request } from '../utils/network';

import { readToken } from '../auth/session';

/**
 * The server's timezone, fetched once and cached in sessionStorage.
 *
 * Split out of GlobalProvider on 22 Aug 2026 (the second slice, after
 * environments): a dozen files across test-plans, load-test and test-logs
 * read this one value, and every one of them re-rendered whenever any of the
 * other ~80 global values changed. The shape follows the design in
 * .kiro/specs/test-plans-global-context-migration.
 *
 * Consumers destructure the same way they did from GlobalContext:
 *   const { serverTimeZone } = useServerTimeZone();
 * The value is null until known — exactly as it was while GlobalProvider's
 * fetch was in flight — so no consumer's null-handling changes.
 */

const CACHE_KEY = 'serverTimeZone';

/* One fetch per session even if several consumers mount together. */
let inflight = null;

const fetchTimeZone = () => {
  if (!inflight) {
    inflight = request('/api/timezone', 'GET')
      .then((response) => {
        const tz = response?.timeZone || null;
        if (tz) sessionStorage.setItem(CACHE_KEY, tz);
        return tz;
      })
      .catch(() => null)
      .finally(() => { inflight = null; });
  }
  return inflight;
};

/** Seed the cache when the timezone is already known — sign-in uses this. */
export const primeServerTimeZone = (timeZone) => {
  if (timeZone) sessionStorage.setItem(CACHE_KEY, timeZone);
};

export function useServerTimeZone() {
  const [serverTimeZone, setServerTimeZone] = useState(() => sessionStorage.getItem(CACHE_KEY));

  useEffect(() => {
    if (serverTimeZone) return undefined;
    // Same guard GlobalProvider had: nothing to ask the server before sign-in.
    if (!readToken()) return undefined;
    let cancelled = false;
    fetchTimeZone().then((tz) => {
      if (!cancelled && tz) setServerTimeZone(tz);
    });
    return () => { cancelled = true; };
  }, [serverTimeZone]);

  return { serverTimeZone };
}

export default useServerTimeZone;
